// Local, offline scoring. Runs instantly with no API. The LLM (when configured)
// adds the "did you actually answer" gate plus point-first / tangent judgment,
// which we blend in via evaluateResponse().

import { BUDGET, FILLER_WORDS, HEDGE_OPENERS } from "./config";

export function splitSentences(text) {
  return text
    .trim()
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function countFillers(text) {
  const lower = " " + text.toLowerCase() + " ";
  let total = 0;
  const hits = [];
  for (const f of FILLER_WORDS) {
    // Word-boundary-ish match that also handles multiword phrases.
    const escaped = f.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(?<![a-z])${escaped}(?![a-z])`, "gi");
    const m = lower.match(re);
    if (m) {
      total += m.length;
      hits.push({ word: f, count: m.length });
    }
  }
  return { total, hits };
}

// 0-100: how well the response fits the word/sentence budget.
function economyScore(words, sentences) {
  const wOver = Math.max(0, words - BUDGET.words);
  const sOver = Math.max(0, sentences - BUDGET.sentences);
  // Each word over budget costs ~1.2 pts; each sentence over costs ~10.
  let score = 100 - wOver * 1.2 - sOver * 10;
  // Also nudge down rambling sentences (long avg sentence length).
  const avgLen = sentences ? words / sentences : words;
  if (avgLen > 22) score -= (avgLen - 22) * 1.5;
  return clamp(score);
}

// 0-100: fewer fillers per 100 words is better.
function fillerScore(fillerTotal, words) {
  if (words === 0) return 0;
  const per100 = (fillerTotal / words) * 100;
  // 0 fillers = 100; ~8 per 100 words ≈ 0.
  return clamp(100 - per100 * 12);
}

// 0-100 heuristic: did the point land in the first sentence, or is it buried
// behind a preamble? The LLM does this better; this is the no-key fallback.
function pointHeuristic(sentences) {
  if (sentences.length === 0) return 0;
  // Normalize: drop punctuation so "Well, honestly," reads as "well honestly".
  const first = sentences[0]
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  for (const opener of HEDGE_OPENERS) {
    if (first === opener || first.startsWith(opener + " ")) {
      return 45; // starts with a hedge/preamble — point is delayed
    }
  }
  // Reward a substantive declarative opener (has some length, ends decisively).
  const firstWords = countWords(sentences[0]);
  if (firstWords < 3) return 60; // too thin to carry the point
  return 90;
}

function clamp(n) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

// Pure-local score. Returns metrics + a composite that leans on the two strong
// heuristics (economy, filler) plus the rough point heuristic.
export function scoreLocal(text) {
  const sentences = splitSentences(text);
  const words = countWords(text);
  const fillers = countFillers(text);

  const economy = economyScore(words, sentences.length);
  const filler = fillerScore(fillers.total, words);
  const point = pointHeuristic(sentences);

  const composite = clamp(0.4 * economy + 0.3 * filler + 0.3 * point);

  return {
    words,
    sentences: sentences.length,
    fillers,
    metrics: { economy, filler, point, tangents: null, onTarget: null },
    composite,
    source: "local",
  };
}

// Merge an optional LLM judgment into the local score.
// llm = { onTarget: bool, pointFirst: 0-100, tangents: 0-100, feedback: string }
export function mergeWithLLM(local, llm) {
  if (!llm) return local;
  const { economy, filler } = local.metrics;
  const point = num(llm.pointFirst, local.metrics.point);
  const tangents = num(llm.tangents, 80);

  let composite = clamp(
    0.3 * economy + 0.2 * filler + 0.25 * point + 0.25 * tangents
  );
  // On-target gate: a tidy non-answer cannot score high.
  if (llm.onTarget === false) composite = Math.min(composite, 40);

  return {
    ...local,
    metrics: { economy, filler, point, tangents, onTarget: llm.onTarget },
    composite,
    feedback: llm.feedback || null,
    source: "llm",
  };
}

function num(v, fallback) {
  return typeof v === "number" && !Number.isNaN(v) ? v : fallback;
}

// Build a short, honest feedback line when the LLM isn't available.
export function localFeedback(result) {
  const parts = [];
  const { words, sentences, fillers, metrics } = result;
  if (words > BUDGET.words)
    parts.push(`${words} words — trim toward ${BUDGET.words}.`);
  else parts.push(`${words} words — inside budget.`);

  if (sentences > BUDGET.sentences)
    parts.push(`${sentences} sentences; aim for ${BUDGET.sentences}.`);

  if (fillers.total > 0) {
    const top = fillers.hits
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map((h) => `"${h.word}"`)
      .join(", ");
    parts.push(`Cut fillers: ${top}.`);
  } else {
    parts.push("No fillers — clean.");
  }

  if (metrics.point < 70)
    parts.push("Lead with your point in sentence one; drop the wind-up.");

  return parts.join(" ");
}
